import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Fills in the two participants of an EXISTING match row that doesn't have
 * any yet — every `createRound` call creates a placeholder match with null
 * participants regardless of round type (see useScheduleBuilder's
 * `assignMatchParticipants` doc comment), and until now there was no way to
 * fix that from the schedule UI. Deliberately a separate, small modal from
 * AddMatchModal.jsx rather than a shared one — that one creates a brand new
 * match under a round_number; this one PATCHes an already-existing row's
 * participant fields, so the two have different `onSave` shapes and neither
 * needs the other's concerns (round context vs. an existing row).
 *
 * Portaled to document.body, same reasoning as MatchSettingsModal.jsx: this
 * is opened from deep inside a per-row button (MatchRow.jsx), nested under
 * GeneratedScheduleStep's `tf-animate-in` wrapper, whose animation touches
 * `transform` and leaves a non-`none` computed value even after finishing —
 * that traps `position: fixed` descendants inside the page's own scrolled
 * content instead of the true viewport, breaking centering. The outer div's
 * `stopPropagation` is separately required — a portal only relocates DOM
 * placement, not the REACT tree synthetic events bubble through, and this
 * modal is still a React child of the row whose own onClick opens Match
 * Details; without it, clicking anything here would also fire that.
 */
const AssignParticipantsModal = ({ isOpen, onClose, row, participantType, fetchAllParticipants, onSave }) => {
  const { theme } = useTheme();
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [participantAId, setParticipantAId] = useState('');
  const [participantBId, setParticipantBId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const entityType = participantType === 'teams' ? 'team' : 'user';
  const entityLabel = participantType === 'teams' ? 'Team' : 'Player';

  useEffect(() => {
    if (!isOpen) return;
    setParticipantAId('');
    setParticipantBId('');
    setError(null);
    setLoading(true);
    fetchAllParticipants()
      .then(setParticipants)
      .finally(() => setLoading(false));
  }, [isOpen, fetchAllParticipants]);

  if (!isOpen) return null;

  const close = () => {
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!participantAId || !participantBId) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave(row, {
        participantAType: entityType,
        participantAId: Number(participantAId),
        participantBType: entityType,
        participantBId: Number(participantBId),
      });
      close();
    } catch (e) {
      setError(e.message || 'Failed to assign teams');
      setIsSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
      <div className={`relative w-full max-w-md rounded-2xl border ${theme.border} ${theme.card} ${theme.shadow} p-6`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`text-lg font-bold ${theme.text}`}>Assign Teams</h3>
          <button
            type="button"
            onClick={close}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
          >
            <X size={16} />
          </button>
        </div>

        <p className={`mb-4 text-sm ${theme.textSecondary}`}>
          This match doesn't have a {entityLabel.toLowerCase()} on either side yet — pick who's playing.
        </p>

        <div className="space-y-4">
          <div>
            <label className={`mb-2 block text-sm font-medium ${theme.text}`}>{entityLabel} A *</label>
            <select
              value={participantAId}
              onChange={(e) => setParticipantAId(e.target.value)}
              disabled={loading}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            >
              <option value="">Select {entityLabel}</option>
              {participants
                .filter((p) => String(p.id) !== String(participantBId))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${theme.text}`}>{entityLabel} B *</label>
            <select
              value={participantBId}
              onChange={(e) => setParticipantBId(e.target.value)}
              disabled={loading}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${theme.input}`}
            >
              <option value="">Select {entityLabel}</option>
              {participants
                .filter((p) => String(p.id) !== String(participantAId))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>

          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            disabled={isSaving}
            className={`rounded-lg border px-4 py-2 text-sm font-medium ${theme.border} ${theme.text} ${theme.buttonGhost} disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!participantAId || !participantBId || isSaving}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {isSaving ? 'Saving…' : 'Assign Teams'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignParticipantsModal;
